import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  GridApi,
  GridOptions,
  ColDef,
  CellClassParams,
  CellValueChangedEvent,
  ITooltipParams,
} from 'ag-grid-community';
import { isNull, isNullOrUndefined, isUndefined } from 'is-what';

import type { CalculateFeeBulkMegaRequest } from '../services/interfaces/calculate-fee-bulk-mega-request';
import type { CalculateFeeMegaResponse } from '../services/interfaces/calculate-fee-mega-response';
import { PassDataService } from '../services/pass-data.service';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { MegaTradeNavigationService } from '../services/mega-trade-navigation.service';
import { MegasService } from '../services/megas.service';

interface MegaGridRow {
  id: number;
  prefix: string | null;
  couponOrSubtype: string | null;
  lowCoupon?: string | null;
  dealAmount: number | null;
  settlementDate: string | null;
}

interface CellError {
  rowId: number;
  field: string;
  message: string;
  type: 'error' | 'info';
}

@Component({
  selector: 'invd-bulk-upload-megas-ag',
  standalone: true,
  imports: [CommonModule, AgGridAngular],
  templateUrl: './bulk-upload-megas-ag.component.html',
  styleUrls: ['./bulk-upload-megas-ag.component.scss'],
})
export class BulkUploadMegasAgComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  public headerText: string;
  public headerSubText: string;
  public isAllowToNavigateAway = false;
  public showWarningMsg = false;
  public selectedMegas = '';
  public selectMegaTypePlaceholder = 'Select';
  public megaTypes: string[] = [];
  public isMegaTypeDropdownOpen = false;
  public calcFeeRequest: CalculateFeeBulkMegaRequest[] = [];
  public reviewButtonEnabled = false;
  public hasValidationError = false;
  public reviewButtonText = 'REVIEW';
  public startOverButtonText = 'START OVER';
  public showStartOverButton = false;
  public showErrorSection = false;
  public validationRes: any = null;
  public totalErrorCount = 0;
  public validationErrorList: any;
  public message = '';
  public count = 0;
  public countOfCopiedRow = 0;
  public entityCatagory: string[] = [];
  public errorResult: any[] = [];
  public isNavigationFromReviewPage = false;
  public showGrid = false;
  public isReviewButtonClicked = true;
  public subTypeListRes: any;

  protected rowData = signal<MegaGridRow[]>([]);
  protected columnDefs = signal<ColDef[]>([]);
  protected cellErrors = signal<CellError[]>([]);
  protected gridApi: GridApi | null = null;
  private pasteEventListener: ((event: ClipboardEvent) => void) | null = null;

  public gridOptions: GridOptions = {
    defaultColDef: {
      editable: true,
      sortable: false,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true,
    },
    rowHeight: 30,
    headerHeight: 35,
    domLayout: 'normal',
    suppressRowClickSelection: true,
    singleClickEdit: true,
    stopEditingWhenCellsLoseFocus: true,
    tooltipShowDelay: 500,
    tooltipMouseTrack: true,
    popupParent: document.body,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    rowSelection: 'multiple',
    suppressRowDeselection: true,
    enableRangeSelection: true,
    enableFillHandle: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
    suppressClipboardPaste: true,
    onGridReady: (params) => {
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();
    },
    onCellValueChanged: (event) => this.onCellValueChanged(event),
    getRowId: (params) => params.data.id.toString(),
    processCellForClipboard: (params) => {
      return params.value;
    },
  };

  public fmLoadingPageService = inject(FmLoadingPageService);
  private router = inject(Router);
  private passDataService = inject(PassDataService);
  private megaNavService = inject(MegaTradeNavigationService);
  private megaService = inject(MegasService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.navigationController();
    this.headerText = StringConstants.MEGA_BULK_UPLOAD_ENTER_DETAILS_SCREEN_HEADER;
    this.headerSubText = StringConstants.MEGA_BULK_UPLOAD_ENTER_DETAILS_SCREEN_SUB_HEADER;
    this.showGrid = false;

    this.isReviewButtonClicked = false;
    this.reviewButtonText = 'REVIEW';
    this.startOverButtonText = 'START OVER';
    this.showErrorSection = false;
    this.megaTypes = [
      StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC,
      StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC,
      StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC,
      StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC,
    ];

    if (
      !isNull(this.passDataService.getMultiMegaResponse()) &&
      !isUndefined(this.passDataService.getMultiMegaResponse()) &&
      this.passDataService.getGoBackEditFlag() === 'true'
    ) {
      this.showStartOverButton = true;
      this.startOverButtonText = 'CANCEL EDIT';
      this.loadDataFromPassDataService();
      this.setSelectedMegaType(this.passDataService.getMegaType());
      this.isNavigationFromReviewPage = true;
    }
  }

  ngAfterViewInit() {
    this.navigationController();
    if (this.isNavigationFromReviewPage) {
      this.handleToolTipMessageForEarlyCloseDate();
    }
    this.setupPasteEventListener();
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('review-and-commit') > 0) {
          this.isAllowToNavigateAway = true;
        } else {
          this.isAllowToNavigateAway = true;
        }
      }
    });
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  private resizeGridColumns(): void {
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 100);
  }

  ngOnDestroy() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.removePasteEventListener();
  }

  public toggleMegaTypeDropdown() {
    this.isMegaTypeDropdownOpen = !this.isMegaTypeDropdownOpen;
  }

  public closeMegaTypeDropdown() {
    this.isMegaTypeDropdownOpen = false;
  }

  public setSelectedMegaType(selectedMegaType: string) {
    this.isMegaTypeDropdownOpen = false;
    this.showGrid = true;
    this.count = 0;
    this.hasValidationError = false;
    this.showErrorSection = false;
    this.showStartOverButton = false;
    this.reviewButtonText = 'REVIEW';
    this.resizeGridColumns();

    if (this.passDataService.getGoBackEditFlag() !== 'true') {
      this.initializeEmptyRowData(selectedMegaType);
    } else if (this.passDataService.getGoBackEditFlag() === 'true') {
      this.showStartOverButton = true;
      this.reviewButtonText = StringConstants.MEGA_ENTER_DETAILS_SCREEN_REVIEW_BUTTON_EDIT_TEXT;
    }

    const isGoBack = this.isNavigationFromReviewPage;
    this.passDataService.setGoBackEditFlag('false');
    this.passDataService.setDropdownSelectedMegaType(this.selectedMegas);
    this.selectedMegas = selectedMegaType;
    if (!isGoBack) {
      this.reviewButtonEnabled = false;
    }

    this.setColumnDefinitions(selectedMegaType);
    this.setEntityCategory(selectedMegaType);
  }

  private initializeEmptyRowData(megaType: string) {
    const emptyRows: MegaGridRow[] = [];
    for (let i = 0; i < 50; i++) {
      if (megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC) {
        emptyRows.push({
          id: i,
          prefix: null,
          couponOrSubtype: null,
          lowCoupon: null,
          dealAmount: null,
          settlementDate: null,
        });
      } else {
        emptyRows.push({
          id: i,
          prefix: null,
          couponOrSubtype: null,
          dealAmount: null,
          settlementDate: null,
        });
      }
    }
    this.rowData.set(emptyRows);
  }

  private setColumnDefinitions(megaType: string) {
    switch (megaType) {
      case StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC:
        this.columnDefs.set([
          this.createRowNumberColumn(),
          this.createPrefixColumn(),
          this.createCouponColumn(StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_COUPON),
          this.createDealAmountColumn(),
          this.createSettlementDateColumn(),
        ]);
        this.entityCatagory = ['Prefix', 'Coupon', 'Deal Amount', 'Settlement Date'];
        break;

      case StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC:
        this.columnDefs.set([
          this.createRowNumberColumn(),
          this.createPrefixColumn(),
          this.createSubtypeColumn(),
          this.createDealAmountColumn(),
          this.createSettlementDateColumn(),
        ]);
        this.entityCatagory = ['Prefix', 'Subtype', 'Deal Amount', 'Settlement Date'];
        break;

      case StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC:
        this.columnDefs.set([
          this.createRowNumberColumn(),
          this.createPrefixColumn(),
          this.createCouponColumn(StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_HIGH_COUPON),
          this.createCouponColumn(
            StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_LOW_COUPON,
            'lowCoupon',
          ),
          this.createDealAmountColumn(),
          this.createSettlementDateColumn(),
        ]);
        this.entityCatagory = [
          'Prefix',
          'High Coupon',
          'Low Coupon',
          'Deal Amount',
          'Settlement Date',
        ];
        break;

      case StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC:
        this.columnDefs.set([
          this.createRowNumberColumn(),
          this.createPrefixColumn(),
          this.createSubtypeColumn(),
          this.createDealAmountColumn(),
          this.createSettlementDateColumn(),
        ]);
        this.entityCatagory = ['Prefix', 'Subtype', 'Deal Amount', 'Settlement Date'];
        break;

      default:
        this.columnDefs.set([]);
        break;
    }
  }

  private setEntityCategory(megaType: string) {
    switch (megaType) {
      case StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC:
        this.entityCatagory = ['Prefix', 'Coupon', 'Deal Amount', 'Settlement Date'];
        break;
      case StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC:
      case StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC:
        this.entityCatagory = ['Prefix', 'Subtype', 'Deal Amount', 'Settlement Date'];
        break;
      case StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC:
        this.entityCatagory = [
          'Prefix',
          'High Coupon',
          'Low Coupon',
          'Deal Amount',
          'Settlement Date',
        ];
        break;
    }
  }

  private createRowNumberColumn(): ColDef {
    return {
      headerName: '',
      valueGetter: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex + 1;
        }
        return '';
      },
      editable: false,
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      sortable: false,
      filter: false,
      suppressNavigable: true,
      cellStyle: {
        backgroundColor: '#f5f5f5',
        fontWeight: '600',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
      },
    };
  }

  private createPrefixColumn(): ColDef {
    return {
      field: 'prefix',
      headerName: StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_PREFIX,
      editable: true,
      width: 120,
      valueSetter: (params) => {
        if (params.newValue) {
          params.data.prefix = params.newValue.toString().slice(0, 2).toUpperCase();
        } else {
          params.data.prefix = params.newValue;
        }
        return true;
      },
      cellClass: (params) => this.getCellErrorClass(params),
      tooltipValueGetter: (params) => this.getCellTooltip(params),
    };
  }

  private createCouponColumn(headerName: string, field: string = 'couponOrSubtype'): ColDef {
    return {
      field,
      headerName,
      editable: true,
      width: 120,
      valueSetter: (params) => {
        if (params.newValue) {
          const numValue = parseFloat(params.newValue);
          if (!isNaN(numValue)) {
            params.data[field] = numValue.toFixed(3);
          } else {
            params.data[field] = params.newValue;
          }
        } else {
          params.data[field] = params.newValue;
        }
        return true;
      },
      cellClass: (params) => this.getCellErrorClass(params),
      tooltipValueGetter: (params) => this.getCellTooltip(params),
    };
  }

  private createSubtypeColumn(): ColDef {
    return {
      field: 'couponOrSubtype',
      headerName: StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_SUBTYPE,
      editable: true,
      width: 120,
      valueSetter: (params) => {
        if (params.newValue) {
          params.data.couponOrSubtype = params.newValue.toString().slice(0, 3).toUpperCase();
        } else {
          params.data.couponOrSubtype = params.newValue;
        }
        return true;
      },
      cellClass: (params) => this.getCellErrorClass(params),
      tooltipValueGetter: (params) => this.getCellTooltip(params),
    };
  }

  private createDealAmountColumn(): ColDef {
    return {
      field: 'dealAmount',
      headerName: StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_DEAL_AMOUNT,
      editable: true,
      type: 'numericColumn',
      valueFormatter: (params) => {
        if (params.value !== null && params.value !== undefined && params.value !== '') {
          const numValue = parseInt(params.value.toString(), 10);
          return !isNaN(numValue) ? '$ ' + numValue.toLocaleString('en') : params.value;
        }
        return '';
      },
      valueSetter: (params) => {
        if (params.newValue) {
          const numValue = parseInt(params.newValue.toString().replace(/[^0-9]/g, ''), 10);
          params.data.dealAmount = !isNaN(numValue) ? numValue : params.newValue;
        } else {
          params.data.dealAmount = params.newValue;
        }
        return true;
      },
      cellClass: (params) => this.getCellErrorClass(params),
      tooltipValueGetter: (params) => this.getCellTooltip(params),
      cellStyle: { textAlign: 'right' },
    };
  }

  private createSettlementDateColumn(): ColDef {
    return {
      field: 'settlementDate',
      headerName: StringConstants.MEGA_BULK_UPLOAD_COLUMN_HEADER_SETTLEMENT_DATE,
      editable: true,
      valueSetter: (params) => {
        if (params.newValue) {
          params.data.settlementDate = this.formatDate(params.newValue);
        } else {
          params.data.settlementDate = params.newValue;
        }
        return true;
      },
      cellClass: (params) => this.getCellErrorClass(params),
      tooltipValueGetter: (params) => this.getCellTooltip(params),
    };
  }

  private formatDate(value: string): string {
    if (!value) return value;

    const dateSplitBySlash = value.toString().split('/');
    const dateSplitByDash = value.toString().split('-');

    let dateParts = dateSplitBySlash;

    if (dateSplitByDash.length > 1 && dateSplitBySlash.length === 1) {
      dateParts = dateSplitByDash;
    }

    if (dateParts.length === 3) {
      let [month, day, year] = dateParts;

      if (month.length === 1) month = '0' + month;
      if (day.length === 1) day = '0' + day;
      if (year.length === 2) year = '20' + year;

      return `${month}/${day}/${year}`;
    }

    return value;
  }

  private getCellErrorClass(params: CellClassParams): string {
    const cellError = this.cellErrors().find(
      (err) => err.rowId === params.data.id && err.field === params.colDef.field,
    );

    if (cellError) {
      return cellError.type === 'error' ? 'cell-error' : 'cell-info';
    }

    return '';
  }

  private getCellTooltip(params: ITooltipParams): string {
    const colDef = params.colDef as ColDef;
    const colId = colDef?.field;
    if (!colId) return '';

    const cellError = this.cellErrors().find(
      (err) => err.rowId === params.data.id && err.field === colId,
    );

    return cellError ? cellError.message : '';
  }

  private onCellValueChanged(event: CellValueChangedEvent): void {
    const newValue = event.newValue;
    const oldValue = event.oldValue;

    if (newValue !== oldValue) {
      const rowData = [...this.rowData()];
      rowData[event.rowIndex] = event.data;
      this.rowData.set(rowData);

      this.validateCellValue(event);
      this.updateReviewButtonState();
    }
  }

  private setupPasteEventListener(): void {
    const gridElement = document.querySelector('.ag-root-wrapper');
    if (!gridElement) {
      setTimeout(() => this.setupPasteEventListener(), 100);
      return;
    }

    this.pasteEventListener = (event: ClipboardEvent) => {
      this.handlePaste(event);
    };

    gridElement.addEventListener('paste', this.pasteEventListener);
  }

  private removePasteEventListener(): void {
    if (this.pasteEventListener) {
      const gridElement = document.querySelector('.ag-root-wrapper');
      if (gridElement) {
        gridElement.removeEventListener('paste', this.pasteEventListener);
      }
      this.pasteEventListener = null;
    }
  }

  private handlePaste(event: ClipboardEvent): void {
    if (!this.gridApi) return;

    this.gridApi.stopEditing();

    const focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) return;

    const clipboardData = event.clipboardData?.getData('text/plain');
    if (!clipboardData) return;

    const rows = clipboardData.split(/\r?\n/).filter((row) => row.trim() !== '');
    if (rows.length === 0) return;

    const pastedData = rows.map((row) => row.split('\t'));
    const numRows = pastedData.length;
    const numCols = pastedData[0]?.length || 0;

    if (numRows >= 1 && numCols >= 1) {
      event.preventDefault();
      event.stopPropagation();

      const startRowIndex = focusedCell.rowIndex;
      const startColKey = focusedCell.column.getColId();

      const allColumns = this.gridApi.getColumns();
      if (!allColumns) return;

      const editableColumns = allColumns.filter(
        (col) => col.getColId() !== 'ag-Grid-AutoColumn' && col.getColDef().editable !== false,
      );

      const startColIndex = editableColumns.findIndex((col) => col.getColId() === startColKey);
      if (startColIndex === -1) return;

      const updatedRowData = [...this.rowData()];

      for (let rowOffset = 0; rowOffset < numRows; rowOffset++) {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex >= updatedRowData.length) break;

        const rowValues = pastedData[rowOffset];
        const updatedRow = { ...updatedRowData[targetRowIndex] };

        for (let colOffset = 0; colOffset < numCols && colOffset < rowValues.length; colOffset++) {
          const targetColIndex = startColIndex + colOffset;
          if (targetColIndex >= editableColumns.length) break;

          const column = editableColumns[targetColIndex];
          const field = column.getColId();
          const value = rowValues[colOffset]?.trim();

          if (field && field !== 'ag-Grid-AutoColumn' && value !== undefined && value !== '') {
            switch (field) {
              case 'prefix':
                updatedRow[field] = value.toString().slice(0, 2).toUpperCase();
                break;
              case 'couponOrSubtype':
                if (
                  this.selectedMegas === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC ||
                  this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC
                ) {
                  const numValue = parseFloat(value);
                  updatedRow[field] = !isNaN(numValue) ? numValue.toFixed(3) : value;
                } else {
                  updatedRow[field] = value.toString().slice(0, 3).toUpperCase();
                }
                break;
              case 'lowCoupon':
                const lowCouponValue = parseFloat(value);
                updatedRow[field] = !isNaN(lowCouponValue) ? lowCouponValue.toFixed(3) : value;
                break;
              case 'dealAmount':
                const dealValue = parseInt(value.toString().replace(/[^0-9]/g, ''), 10);
                updatedRow[field] = !isNaN(dealValue) ? dealValue : null;
                break;
              case 'settlementDate':
                updatedRow[field] = this.formatDate(value);
                break;
              default:
                updatedRow[field] = value;
                break;
            }
          }
        }

        updatedRowData[targetRowIndex] = updatedRow;
      }

      this.rowData.set(updatedRowData);

      const updatedRows = updatedRowData.slice(startRowIndex, startRowIndex + numRows);
      this.gridApi.applyTransaction({ update: updatedRows });

      this.updateReviewButtonState();
    }
  }

  private validateCellValue(event: CellValueChangedEvent): void {
    const field = event.colDef.field;
    const value = event.newValue;
    const rowId = event.data.id;

    const currentErrors = this.cellErrors().filter(
      (err) => !(err.rowId === rowId && err.field === field),
    );

    if (!value || value === '') {
      this.cellErrors.set(currentErrors);
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
      return;
    }

    let errorMessage = '';
    let hasError = false;

    switch (field) {
      case 'prefix':
        if (value.length !== 2) {
          errorMessage = 'Prefix must be exactly 2 characters';
          hasError = true;
        }
        break;

      case 'couponOrSubtype':
        if (this.selectedMegas === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC) {
          const couponValue = parseFloat(value);
          if (isNaN(couponValue)) {
            errorMessage = 'Coupon must be a numeric value';
            hasError = true;
          } else if (couponValue < 0 || couponValue > 100) {
            errorMessage = 'Coupon must be between 0 and 100';
            hasError = true;
          }
        } else if (
          this.selectedMegas === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC ||
          this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC
        ) {
          if (value.length < 2 || value.length > 3) {
            errorMessage = 'Subtype must be 2-3 characters';
            hasError = true;
          }
        } else if (this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC) {
          const couponValue = parseFloat(value);
          if (isNaN(couponValue)) {
            errorMessage = 'High Coupon must be a numeric value';
            hasError = true;
          } else if (couponValue < 0 || couponValue > 100) {
            errorMessage = 'High Coupon must be between 0 and 100';
            hasError = true;
          }
        }
        break;

      case 'lowCoupon':
        const lowCouponValue = parseFloat(value);
        if (isNaN(lowCouponValue)) {
          errorMessage = 'Low Coupon must be a numeric value';
          hasError = true;
        } else if (lowCouponValue < 0 || lowCouponValue > 100) {
          errorMessage = 'Low Coupon must be between 0 and 100';
          hasError = true;
        } else {
          const highCoupon = event.data.couponOrSubtype;
          if (highCoupon) {
            const highCouponValue = parseFloat(highCoupon);
            if (!isNaN(highCouponValue) && lowCouponValue > highCouponValue) {
              errorMessage = 'Low Coupon cannot be greater than High Coupon';
              hasError = true;
            }
          }
        }
        break;

      case 'dealAmount':
        const dealAmountValue =
          typeof value === 'number' ? value : parseInt(value.toString().replace(/[^0-9]/g, ''), 10);
        if (isNaN(dealAmountValue)) {
          errorMessage = 'Deal Amount must be a numeric value';
          hasError = true;
        } else if (dealAmountValue < 1000) {
          errorMessage = 'Deal Amount must be at least $1,000';
          hasError = true;
        }
        break;

      case 'settlementDate':
        const datePattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
        if (!datePattern.test(value)) {
          errorMessage = 'Settlement Date must be in MM/DD/YYYY format';
          hasError = true;
        } else {
          const [month, day, year] = value.split('/').map(Number);
          const date = new Date(year, month - 1, day);
          if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
          ) {
            errorMessage = 'Settlement Date is not a valid date';
            hasError = true;
          }
        }
        break;
    }

    if (hasError && field) {
      currentErrors.push({
        rowId,
        field,
        message: errorMessage,
        type: 'error',
      });
    }

    this.cellErrors.set(currentErrors);

    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  private updateReviewButtonState() {
    const rows = this.rowData();
    const nonEmptyRows = rows.filter(
      (row) =>
        (row.prefix && row.prefix.trim() !== '') ||
        (row.couponOrSubtype && row.couponOrSubtype.toString().trim() !== '') ||
        (row.lowCoupon && row.lowCoupon.toString().trim() !== '') ||
        (row.dealAmount !== null && row.dealAmount !== 0) ||
        (row.settlementDate && row.settlementDate.trim() !== ''),
    );

    this.countOfCopiedRow = nonEmptyRows.length;
    const isValid = this.validateRowsClientSide(nonEmptyRows);
    this.reviewButtonEnabled = nonEmptyRows.length > 0 && isValid;
  }

  private validateRowsClientSide(rows: MegaGridRow[]): boolean {
    const currentErrors = this.cellErrors().filter((err) => err.type === 'error');

    if (currentErrors.length > 0) {
      return false;
    }

    for (const row of rows) {
      if (this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC) {
        if (
          !row.prefix ||
          !row.couponOrSubtype ||
          !row.lowCoupon ||
          !row.dealAmount ||
          !row.settlementDate
        ) {
          return false;
        }
      } else if (!row.prefix || !row.couponOrSubtype || !row.dealAmount || !row.settlementDate) {
        return false;
      }

      if (row.prefix && row.prefix.length !== 2) {
        return false;
      }

      const isArmType =
        this.selectedMegas === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC ||
        this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC;

      if (
        isArmType &&
        row.couponOrSubtype &&
        (row.couponOrSubtype.length < 2 || row.couponOrSubtype.length > 3)
      ) {
        return false;
      }

      if (row.dealAmount && row.dealAmount < 1000) {
        return false;
      }

      if (
        row.settlementDate &&
        !(
          /^\d{2}\/\d{2}\/\d{4}$/.test(row.settlementDate) ||
          /^\d{2}\-\d{2}\-\d{4}$/.test(row.settlementDate)
        )
      ) {
        return false;
      }
    }

    return true;
  }

  public validateMultiMega(): void {
    if (this.isReviewButtonClicked) {
      return;
    }

    this.isReviewButtonClicked = true;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.calcFeeRequest = [];

    const rows = this.rowData();
    const nonEmptyRows = rows.filter(
      (row) =>
        (row.prefix && row.prefix.trim() !== '') ||
        (row.couponOrSubtype && row.couponOrSubtype.toString().trim() !== '') ||
        (row.lowCoupon && row.lowCoupon.toString().trim() !== '') ||
        (row.dealAmount !== null && row.dealAmount !== 0) ||
        (row.settlementDate && row.settlementDate.trim() !== ''),
    );

    this.buildCalcFeeRequest(nonEmptyRows);

    if (this.calcFeeRequest !== null && this.calcFeeRequest.length > 0) {
      const req: any = {
        packageRevisionNumber: null,
        megasDetails: this.calcFeeRequest,
      };
      this.passDataService.setMultiMegaRequest(req);
      this.megaService.multiMegaValidate(req).subscribe({
        next: (data) => this.handleValidateServiceSuccess(data as CalculateFeeMegaResponse),
        error: (error) => this.handleValidateServiceFail(error),
      });
    } else {
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.isReviewButtonClicked = false;
    }
  }

  private buildCalcFeeRequest(rows: MegaGridRow[]): void {
    rows.forEach((row) => {
      const mBundle: any = {} as CalculateFeeBulkMegaRequest;
      mBundle.rowNumber = row.id + 1;

      switch (this.selectedMegas) {
        case StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC:
          mBundle.megaType = 'MFFIXMEGA';
          mBundle.prefixCode = row.prefix ? row.prefix.toString().slice(0, 2) : null;
          mBundle.mfHighCoupon = row.couponOrSubtype
            ? parseFloat(row.couponOrSubtype.toString()).toFixed(3)
            : null;
          mBundle.mfLowCoupon = row.lowCoupon
            ? parseFloat(row.lowCoupon.toString()).toFixed(3)
            : null;
          mBundle.tradeAmount = row.dealAmount ? row.dealAmount.toString() : null;
          mBundle.tradeSettlementDate = row.settlementDate
            ? this.setDateFormat(row.settlementDate)
            : null;
          mBundle.sfARMSubType = null;
          mBundle.sfFixedCoupon = null;
          break;

        case StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC:
          mBundle.megaType = 'SFFIXMEGA';
          mBundle.prefixCode = row.prefix ? row.prefix.toString().slice(0, 2) : null;
          mBundle.sfFixedCoupon = row.couponOrSubtype
            ? parseFloat(row.couponOrSubtype.toString()).toFixed(3)
            : null;
          mBundle.tradeAmount = row.dealAmount ? row.dealAmount.toString() : null;
          mBundle.tradeSettlementDate = row.settlementDate
            ? this.setDateFormat(row.settlementDate)
            : null;
          mBundle.mfHighCoupon = null;
          mBundle.mfLowCoupon = null;
          mBundle.sfARMSubType = null;
          break;

        case StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC:
          mBundle.megaType = 'SFARMMEGA';
          mBundle.prefixCode = row.prefix ? row.prefix.toString().slice(0, 2) : null;
          mBundle.sfARMSubType = row.couponOrSubtype
            ? row.couponOrSubtype.toString().slice(0, 3)
            : null;
          mBundle.tradeAmount = row.dealAmount ? row.dealAmount.toString() : null;
          mBundle.tradeSettlementDate = row.settlementDate
            ? this.setDateFormat(row.settlementDate)
            : null;
          mBundle.mfHighCoupon = null;
          mBundle.mfLowCoupon = null;
          mBundle.sfFixedCoupon = null;
          break;

        case StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC:
          mBundle.megaType = 'MFARMMEGA';
          mBundle.prefixCode = row.prefix ? row.prefix.toString().slice(0, 2) : null;
          mBundle.sfARMSubType = row.couponOrSubtype
            ? row.couponOrSubtype.toString().slice(0, 3)
            : null;
          mBundle.tradeAmount = row.dealAmount ? row.dealAmount.toString() : null;
          mBundle.tradeSettlementDate = row.settlementDate
            ? this.setDateFormat(row.settlementDate)
            : null;
          mBundle.mfHighCoupon = null;
          mBundle.mfLowCoupon = null;
          mBundle.sfFixedCoupon = null;
          break;
      }

      mBundle.feeTicks = null;
      mBundle.feeDollars = null;
      this.calcFeeRequest.push(mBundle);
    });
  }

  private handleValidateServiceSuccess(calcFee: any) {
    this.errorResult = [];
    this.totalErrorCount = 0;
    this.validationRes = calcFee;
    this.hasValidationError = false;
    this.passDataService.setMultiMegaValidationResponse(calcFee);

    if (this.validationRes.errorDetail !== null) {
      this.validationRes.errorDetail.forEach((item: any) => {
        if (item.messageType === 'error') {
          this.hasValidationError = true;
        }
      });
    }

    if (this.hasValidationError) {
      this.processValidationErrors(calcFee);
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.hasValidationError = true;
      this.showErrorSection = true;
      this.reviewButtonText = 'REVIEW';
      this.showStartOverButton = true;
      this.startOverButtonText = 'START OVER';
      this.isReviewButtonClicked = false;
      this.cdr.markForCheck();
      this.resizeGridColumns();
      return;
    } else {
      const req: any = this.passDataService.getMultiMegaRequest();
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.cdr.markForCheck();
      this.getMultiMegaCalculateFee(req);
    }
  }

  private processValidationErrors(calcFee: any) {
    const errorDetail = calcFee && calcFee.errorSummary;
    this.validationErrorList = calcFee;

    this.entityCatagory.forEach((key) => {
      let itemPresent = false;
      errorDetail.filter((item: any) => {
        if (!itemPresent && item.entityCategory === key) {
          this.errorResult.push(item);
          itemPresent = true;
          return false;
        }
        return true;
      });
    });

    this.totalErrorCount = calcFee.errorDetail.length;

    const newCellErrors: CellError[] = [];

    calcFee.errorDetail.forEach((item: any) => {
      const field = this.getFieldFromEntityType(item.entityType);
      if (item.rowNumber !== null) {
        newCellErrors.push({
          rowId: item.rowNumber - 1,
          field,
          message: item.toolTipMessage,
          type: item.messageType === 'info' ? 'info' : 'error',
        });
      }
    });

    this.cellErrors.set(newCellErrors);

    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  private getFieldFromEntityType(entityType: string): string {
    if (this.selectedMegas === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC) {
      switch (entityType) {
        case 'Prefix':
          return 'prefix';
        case 'Coupon':
          return 'couponOrSubtype';
        case 'Deal Amount':
          return 'dealAmount';
        case 'Settlement Date':
          return 'settlementDate';
        default:
          return '';
      }
    } else if (
      this.selectedMegas === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC ||
      this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC
    ) {
      switch (entityType) {
        case 'Prefix':
          return 'prefix';
        case 'Subtype':
          return 'couponOrSubtype';
        case 'Deal Amount':
          return 'dealAmount';
        case 'Settlement Date':
          return 'settlementDate';
        default:
          return '';
      }
    } else if (this.selectedMegas === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC) {
      switch (entityType) {
        case 'Prefix':
          return 'prefix';
        case 'High Coupon':
          return 'couponOrSubtype';
        case 'Low Coupon':
          return 'lowCoupon';
        case 'Deal Amount':
          return 'dealAmount';
        case 'Settlement Date':
          return 'settlementDate';
        default:
          return '';
      }
    }
    return '';
  }

  public handleValidateServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.isReviewButtonClicked = false;
    this.cdr.markForCheck();
  }

  private getMultiMegaCalculateFee(req: any): void {
    this.megaService.multiMegaCalculateFee(req).subscribe({
      next: (data) => this.handleCalculateFeeServiceSuccess(data as CalculateFeeMegaResponse),
      error: (error) => this.handleAWSCalculateFeeServiceFail(error),
    });
  }

  private handleCalculateFeeServiceSuccess(calcFee: CalculateFeeMegaResponse) {
    this.passDataService.setMultiMegaResponse(calcFee);
    this.megaNavService.canNavigate = true;
    this.cdr.markForCheck();
    this.router.navigate(['/bulk-upload/enter-mega-details/review-and-commit']);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  private handleAWSCalculateFeeServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public setDateFormat(val: string): string {
    let dateForm: any;
    let updatedDateValue: any;

    if (val !== null && /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(val)) {
      updatedDateValue = val && val.split('/');
      if (
        updatedDateValue[0].length === 1 &&
        updatedDateValue[1].length === 1 &&
        updatedDateValue[2].length === 2
      ) {
        updatedDateValue[0] = '0' + updatedDateValue[0];
        updatedDateValue[1] = '0' + updatedDateValue[1];
        updatedDateValue[2] = '20' + updatedDateValue[2];
        val = [updatedDateValue[0], updatedDateValue[1], updatedDateValue[2]].join('/');
      } else if (updatedDateValue[0].length === 1) {
        updatedDateValue[0] = '0' + updatedDateValue[0];
        if (updatedDateValue[2].length === 2) {
          updatedDateValue[2] = '20' + updatedDateValue[2];
        }
        val = [updatedDateValue[0], updatedDateValue[1], updatedDateValue[2]].join('/');
      } else if (updatedDateValue[1].length === 1) {
        updatedDateValue[1] = '0' + updatedDateValue[1];
        if (updatedDateValue[2].length === 2) {
          updatedDateValue[2] = '20' + updatedDateValue[2];
        }
        val = [updatedDateValue[0], updatedDateValue[1], updatedDateValue[2]].join('/');
      } else if (updatedDateValue[2].length === 2) {
        updatedDateValue[2] = '20' + updatedDateValue[2];
        val = [updatedDateValue[0], updatedDateValue[1], updatedDateValue[2]].join('/');
      }
      return val;
    } else if (val !== null && /^\d{1,2}\-\d{1,2}\-\d{2,4}$/.test(val)) {
      dateForm = val && val.split('-');
      if (dateForm[0].length === 1 && dateForm[1].length === 1 && dateForm[2].length === 2) {
        dateForm[0] = '0' + dateForm[0];
        dateForm[1] = '0' + dateForm[1];
        dateForm[2] = '20' + dateForm[2];
      } else if (dateForm[0].length === 1) {
        dateForm[0] = '0' + dateForm[0];
        if (dateForm[2].length === 2) {
          dateForm[2] = '20' + dateForm[2];
        }
      } else if (dateForm[1].length === 1) {
        dateForm[1] = '0' + dateForm[1];
        if (dateForm[2].length === 2) {
          dateForm[2] = '20' + dateForm[2];
        }
      } else if (dateForm[2].length === 2) {
        dateForm[2] = '20' + dateForm[2];
      }
      val = [dateForm[0], dateForm[1], dateForm[2]].join('/');
      return val;
    } else {
      return val;
    }
  }

  public clearTableData(): void {
    this.count = 0;
    this.reviewButtonEnabled = false;
    this.cellErrors.set([]);
    this.initializeEmptyRowData(this.selectedMegas);
    this.countOfCopiedRow = 0;
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  public closeMessageBanner(): void {
    this.hasValidationError = false;
  }

  public callStartOver(): void {
    if (this.startOverButtonText === 'START OVER') {
      this.selectedMegas = '';
      this.showGrid = false;
      this.selectMegaTypePlaceholder = 'Select';
      this.hasValidationError = false;
      this.showErrorSection = false;
      this.reviewButtonText = 'REVIEW';
      this.showStartOverButton = false;
      this.clearTableData();
      this.resizeGridColumns();
    } else if (this.startOverButtonText === 'CANCEL EDIT') {
      this.router.navigate(['/bulk-upload/enter-mega-details/review-and-commit']);
    }
  }

  public proceedChangeMegaType(): void {
    this.clearTableData();
    this.showWarningMsg = false;
  }

  public cancelChangeMegaType(): void {
    if (this.showWarningMsg === true) {
      this.setSelectedMegaType(this.passDataService.getDropdownSelectedMegaType());
    }
    this.showWarningMsg = false;
  }

  private loadDataFromPassDataService() {
    const multiMegaResponse = this.passDataService.getMultiMegaResponse();
    if (
      multiMegaResponse &&
      multiMegaResponse.megaPricingInfo &&
      multiMegaResponse.megaPricingInfo.length > 0
    ) {
      const newMegaList = multiMegaResponse.megaPricingInfo[0].newMegaList;
      const megaTypeDesc = this.passDataService.getMegaType();

      if (newMegaList && newMegaList.length > 0) {
        const megaRows: MegaGridRow[] = newMegaList.map((item: any, index: number) => {
          const row: MegaGridRow = {
            id: index,
            prefix: item.prefix || null,
            couponOrSubtype: null,
            dealAmount: item.tradeAmount != null ? item.tradeAmount : null,
            settlementDate: this.formatApiDate(item.tradeSettlementDate),
          };

          if (megaTypeDesc === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC) {
            row.couponOrSubtype =
              item.mfHighCoupon != null ? Number(item.mfHighCoupon).toFixed(3) : null;
            row.lowCoupon = item.mfLowCoupon != null ? Number(item.mfLowCoupon).toFixed(3) : null;
          } else if (megaTypeDesc === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC) {
            row.couponOrSubtype =
              item.sfFixedCoupon != null ? Number(item.sfFixedCoupon).toFixed(3) : null;
          } else {
            row.couponOrSubtype = item.sfARMSubType || null;
          }

          return row;
        });

        const totalRows = 50;
        const isMultiFamilyFix = megaTypeDesc === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC;
        for (let i = megaRows.length; i < totalRows; i++) {
          const emptyRow: MegaGridRow = {
            id: i,
            prefix: null,
            couponOrSubtype: null,
            dealAmount: null,
            settlementDate: null,
          };
          if (isMultiFamilyFix) {
            emptyRow.lowCoupon = null;
          }
          megaRows.push(emptyRow);
        }

        this.rowData.set(megaRows);
        setTimeout(() => {
          this.updateReviewButtonState();
        });
      }
    }
  }

  private formatApiDate(dateValue: any): string | null {
    if (!dateValue) return null;
    const dateStr = dateValue.toString();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
    }
    return this.formatDate(dateStr);
  }

  public handleToolTipMessageForEarlyCloseDate() {
    if (
      !isNullOrUndefined(this.passDataService.getMultiMegaValidationResponse()) &&
      this.passDataService.getMultiMegaValidationResponse().errorDetail !== null
    ) {
      const validatedMegaList = this.passDataService.getMultiMegaValidationResponse().errorDetail;
      const infoCellErrors: CellError[] = [];

      validatedMegaList.forEach((item) => {
        if (item.messageType === 'info') {
          const field = this.getFieldFromEntityType(item.entityType);
          infoCellErrors.push({
            rowId: item.rowNumber - 1,
            field,
            message: item.toolTipMessage,
            type: 'info',
          });
        }
      });

      this.cellErrors.set([...this.cellErrors(), ...infoCellErrors]);
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    }
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
